import {StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {useTimeout} from "@khanacademy/wonder-blocks-timing";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {useSendChatMessage} from "@khan/guide-core";
import {setPromptVariables} from "@khan/guide-core-helpers";
import {GuideStateApi} from "@khan/guide-state";
import {doNotTranslate} from "@khan/i18n";

import {useBlooketAutosave} from "../hooks/use-blooket-autosave.ts";
import {useGetBlooketQuestionSet} from "../hooks/use-get-blooket-question-set-query.ts";
import type {AutosavingStatus, ToolPersona} from "../types.ts";

import {
    ANIMATION_DURATION,
    CARD_HEIGHT,
    CARD_WIDTH,
    REFILL_THRESHOLD,
} from "./constants.ts";
import Deck from "./deck.tsx";
import QuestionSet from "./question-set.tsx";
import strings from "./strings.ts";
import type {
    BlooketQuestion,
    QuestionSetAnimation,
    QuizContent,
} from "./types.ts";

interface Props {
    persona: ToolPersona;
    setSavingStatus: (savingStatus: AutosavingStatus) => void;
    setQuestionSetContent: (newContent: QuizContent) => void;
    searchParams: string;
}

const Staging: React.FC<Props> = ({
    persona,
    setSavingStatus,
    setQuestionSetContent,
    searchParams,
}: Props) => {
    // Questions are those that the user has accepted.
    const [questions, setQuestions] = React.useState<BlooketQuestion[]>([]);
    // Previously generated questions are those that the AI has generated.
    const previouslyGeneratedQuestions = React.useRef<BlooketQuestion[]>([]);
    const numRunsSoFar = React.useRef<number>(0);
    const [title, setTitle] = React.useState<string>("");
    const [description, setDescription] = React.useState<string>("");
    const sendChatMessage = useSendChatMessage();
    // This ref should be true if there are unsaved changes to the question set
    const unsavedChanges = React.useRef<boolean>(false);
    const getBlooketQuestionSet = useGetBlooketQuestionSet();

    // Whenever questions, title, or description change, indicate that there
    // are unsaved changes.
    // Note: This saves as soon as the AI returns title and description, even
    // if the user has not accepted any question drafts.
    React.useEffect(() => {
        unsavedChanges.current = true;
        setQuestionSetContent({
            questions,
            title,
            description,
        });
    }, [questions, title, description, setQuestionSetContent]);

    const clearUnsavedChanges = () => (unsavedChanges.current = false);

    useBlooketAutosave(
        persona,
        setSavingStatus,
        title,
        description,
        questions,
        unsavedChanges.current,
        clearUnsavedChanges,
    );

    // TO DO (bcdirito):
    // write tests for this functionality and component
    React.useEffect(() => {
        if (new URLSearchParams(searchParams).has("resumeThreadId")) {
            getBlooketQuestionSet().then((quizContent) => {
                if (quizContent) {
                    setTitle(quizContent.title);
                    setDescription(quizContent.description);
                    setQuestions(quizContent.questions);
                }
            });
        }
    }, [getBlooketQuestionSet, searchParams]);

    // Send a request for more questions to the LLM.
    // This should be wrapped in a React callback to avoid excessive
    // re-rendering as the response streams in.
    const refill: () => Promise<void> = React.useCallback(async () => {
        // We need to wait for the guide to be initialized before we can send a
        // request to the LLM. Since this gets called on mount, we can't be
        // sure that initialization is complete by the time this function runs.
        await GuideStateApi.latest().initializationPromise;

        const refillCommand = doNotTranslate`Generate Blooket questions.`;
        setPromptVariables({
            previouslyGeneratedQuestions: JSON.stringify(
                previouslyGeneratedQuestions.current,
            ),
            numRunsSoFar: numRunsSoFar.current.toString(),
        });

        return sendChatMessage(refillCommand, {
            containsSpeechToTextContent: false,
            guideVoiceDescriptor: null,
            // TODO (audrey) there isn't a great existing requesting
            // type to describe a background request.
            // Create a new one?
            requestUIType: "ACTION_BUBBLE",
            prepopulatedText: refillCommand,
            requestWithTextToSpeech: false,
            timeToSubmit: 0,
        });
    }, [sendChatMessage]);

    // Parse a response from the LLM.
    // This is used to parse both the initial and any subsequent responses.
    // Return a set of "cards" for the Deck.
    const parseResponseMessage = (messageText: string) => {
        try {
            // The LLM may wrap the response JSON like this: "```json{...}```".
            // Strip prefix and suffix so it can be parsed.
            // TODO: Move this logic to the backend.
            const cleanText = messageText
                .replace(/^(```json)/, "")
                .replace(/(```)$/, "");
            const parsedResponse = JSON.parse(cleanText) as QuizContent;
            // Set title and description if this is the first response from
            // the LLM and the title hasn't been set yet.
            setTitle((title) => (title === "" ? parsedResponse.title : title));
            setDescription((description) =>
                description === "" ? parsedResponse.description : description,
            );
            previouslyGeneratedQuestions.current =
                previouslyGeneratedQuestions.current.concat(
                    parsedResponse.questions,
                );
            numRunsSoFar.current += 1;

            return parsedResponse.questions;
        } catch {
            // If there is an error parsing the JSON, return an empty set.
            // The Deck will handle retries.
            return [];
        }
    };

    const [animation, setAnimation] =
        React.useState<QuestionSetAnimation>("NONE");
    const action = React.useCallback(() => {
        setAnimation("NONE");
    }, []);

    const {set, isSet} = useTimeout(action, ANIMATION_DURATION);
    React.useEffect(() => {
        if (animation !== "NONE" && !isSet) {
            set();
        }
    }, [set, isSet, animation]);

    const saveQuestion = (q: BlooketQuestion): void => {
        setAnimation("ADDING");
        setQuestions((questions) => [q, ...questions]);
    };

    return (
        <View style={styles.staging}>
            <View>
                <Deck
                    refillThreshold={REFILL_THRESHOLD}
                    keep={saveQuestion}
                    refill={refill}
                    parseResponseMessage={parseResponseMessage}
                    cardWidth={CARD_WIDTH}
                    cardHeight={CARD_HEIGHT}
                />
            </View>
            <Strut size={spacing.medium_16} />
            <QuestionSet
                title={title || strings.defaultTitle()}
                description={description || strings.defaultDescription()}
                questions={questions}
                setQuestions={setQuestions}
                cardWidth={CARD_WIDTH}
                cardHeight={CARD_HEIGHT}
                setTitle={setTitle}
                setDescription={setDescription}
                animation={animation}
            />
        </View>
    );
};

const styles = StyleSheet.create({
    staging: {
        flexDirection: "row",
    },
});

export default Staging;
